import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/features/auth/public";
import { useSimNetworkBridge } from "@/features/sim-network-bridge/public";

/**
 * 全ページ共通のヘッダ＋ Outlet レイアウト。
 *
 * ルート切替は HashRouter 配下で `<NavLink>` ベースの Tab を使用。
 * 子ページはこの下の Box（flex:1）に流し込まれる。
 *
 * `useSimNetworkBridge` をここで一度だけマウントすることで、
 * どのページにいても sim ↔ network が連携し続ける。
 */
export function AppLayout() {
  useSimNetworkBridge();
  const { pathname } = useLocation();
  const current = pathname === "/network" ? "/network" : pathname === "/editor" ? "/editor" : "/";
  const navigate = useNavigate();
  const username = useAuthStore((s) => s.username);
  const logout = useAuthStore((s) => s.logout);

  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const userMenuOpen = Boolean(userMenuAnchor);
  const closeUserMenu = () => setUserMenuAnchor(null);

  const handleLogout = () => {
    closeUserMenu();
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 2,
          px: 2,
          height: 48,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: "#ffffff",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Factory Digital Twin
        </Typography>
        <Tabs value={current} sx={{ minHeight: 40 }} textColor="primary" indicatorColor="primary">
          <Tab
            value="/"
            component={NavLink}
            to="/"
            label="工場シミュレータ"
            icon={<PrecisionManufacturingRoundedIcon fontSize="small" />}
            iconPosition="start"
            sx={{ minHeight: 40, textTransform: "none", fontWeight: 700 }}
          />
          <Tab
            value="/editor"
            component={NavLink}
            to="/editor"
            label="エディタ"
            icon={<EditRoundedIcon fontSize="small" />}
            iconPosition="start"
            sx={{ minHeight: 40, textTransform: "none", fontWeight: 700 }}
          />
          <Tab
            value="/network"
            component={NavLink}
            to="/network"
            label="ネットワーク"
            icon={<HubRoundedIcon fontSize="small" />}
            iconPosition="start"
            sx={{ minHeight: 40, textTransform: "none", fontWeight: 700 }}
          />
        </Tabs>
        <Box sx={{ flex: 1 }} />
        {username ? (
          <>
            <Button
              size="small"
              color="inherit"
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              startIcon={<AccountCircleRoundedIcon fontSize="small" />}
              endIcon={<ArrowDropDownRoundedIcon fontSize="small" />}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen ? true : undefined}
              sx={{
                textTransform: "none",
                color: "text.primary",
                fontWeight: 600,
              }}
            >
              {username}
            </Button>
            <Menu
              anchorEl={userMenuAnchor}
              open={userMenuOpen}
              onClose={closeUserMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{ paper: { sx: { mt: 0.5, minWidth: 180 } } }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  サインイン中
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {username}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>ログアウト</ListItemText>
              </MenuItem>
            </Menu>
          </>
        ) : null}
      </Stack>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
